import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { FlowRule, FlowSurface } from '@/pages/terminal-flows/flow-vocab';

export type FlowStatus = 'draft' | 'published';

export type TerminalFlow = {
  id: string;
  surface: FlowSurface;
  locationId: string | null;
  name: string;
  status: FlowStatus;
  version: number;
  rules: FlowRule[];
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type FlowListResponse = {
  flows: TerminalFlow[];
  page: number;
  pageSize: number;
  total: number;
};

const LIST_KEY = ['terminal-flows'] as const;
const ONE_KEY = (id: string) => ['terminal-flows', id] as const;

export function useTerminalFlows() {
  return useQuery({
    queryKey: LIST_KEY,
    queryFn: async (): Promise<FlowListResponse> => {
      const res = await apiClient.get<FlowListResponse>('/terminal-flows', {
        params: { pageSize: 200 },
      });
      return res.data;
    },
  });
}

export function useTerminalFlow(id: string | undefined) {
  return useQuery({
    queryKey: id === undefined ? ['terminal-flows', '__noop'] : ONE_KEY(id),
    enabled: id !== undefined,
    queryFn: async (): Promise<TerminalFlow> => {
      const res = await apiClient.get<TerminalFlow>(`/terminal-flows/${id}`);
      return res.data;
    },
  });
}

export type CreateFlowInput = {
  surface: FlowSurface;
  name: string;
  locationId?: string | null;
  rules?: FlowRule[];
};

export function useCreateFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateFlowInput): Promise<TerminalFlow> => {
      const res = await apiClient.post<TerminalFlow>('/terminal-flows', input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  });
}

export function useSaveFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      id: string;
      expectedVersion: number;
      name?: string;
      rules: FlowRule[];
    }): Promise<TerminalFlow> => {
      const res = await apiClient.put<TerminalFlow>(`/terminal-flows/${vars.id}`, {
        expectedVersion: vars.expectedVersion,
        name: vars.name,
        rules: vars.rules,
      });
      return res.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: ONE_KEY(data.id) });
    },
  });
}

export function usePublishFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<TerminalFlow> => {
      const res = await apiClient.post<TerminalFlow>(`/terminal-flows/${id}/publish`, {});
      return res.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: ONE_KEY(data.id) });
    },
  });
}

export function useDeleteFlow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await apiClient.delete(`/terminal-flows/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: LIST_KEY }),
  });
}
